import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';

const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('https://unpkg.com/three@0.160.0/examples/jsm/libs/draco/');

const sharedGltfLoader = new GLTFLoader();
sharedGltfLoader.setDRACOLoader(dracoLoader);
sharedGltfLoader.setMeshoptDecoder(MeshoptDecoder);

const modelCache = new Map();

function loadModel(url) {
  if (modelCache.has(url)) return modelCache.get(url);

  const promise = new Promise((resolve, reject) => {
    sharedGltfLoader.load(url, resolve, undefined, reject);
  });

  modelCache.set(url, promise);
  return promise;
}

function createModelViewer({
  container,
  url,
  modelScale,
  modelYOffset,
  isProductModeFn,
  onModelReady
}) {
  const wrapper = document.createElement('div');
  wrapper.className = 'look-item-3d';
  container.appendChild(wrapper);

  const renderer = new THREE.WebGLRenderer({
    alpha: true,
    antialias: true,
    powerPreference: 'high-performance'
  });

  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  wrapper.appendChild(renderer.domElement);

  const scene = new THREE.Scene();

  const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
  camera.position.set(0, 0, 4);

  scene.add(new THREE.AmbientLight(0xffffff, 0.85));

  const keyLight = new THREE.DirectionalLight(0xffffff, 1.4);
  keyLight.position.set(2, 3, 4);
  scene.add(keyLight);

  const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
  fillLight.position.set(-2, 1, -3);
  scene.add(fillLight);

  const pivot = new THREE.Group();
  scene.add(pivot);

  let mixer = null;
  let modelLoaded = false;

  function resize() {
    const w = Math.max(1, wrapper.offsetWidth);
    const h = Math.max(1, wrapper.offsetHeight);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }

  function fitModelToView(object) {
    const box = new THREE.Box3().setFromObject(object);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());

    object.position.x -= center.x;
    object.position.y -= center.y;
    object.position.z -= center.z;
    object.position.y += (modelYOffset / 100) * size.y;

    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const targetSize = 2.0 * (modelScale / 100);
    const fitScale = targetSize / maxDim;

    object.scale.setScalar(fitScale);
  }

  loadModel(url)
    .then((gltf) => {
      const model = cloneSkeleton(gltf.scene);
      pivot.add(model);
      fitModelToView(model);

      if (gltf.animations && gltf.animations.length > 0) {
        mixer = new THREE.AnimationMixer(model);

        gltf.animations.forEach((clip) => {
          mixer.clipAction(clip).play();
        });
      }

      modelLoaded = true;
      resize();

      if (typeof onModelReady === 'function') {
        onModelReady();
      }
    })
    .catch((err) => {
      console.error('[Lookbook] Failed to load GLB:', url, err);
    });

  const ro = new ResizeObserver(resize);
  ro.observe(wrapper);
  resize();

  const clock = new THREE.Clock();
  let rafId = null;

  function tick() {
    const dt = clock.getDelta();

    if (mixer) mixer.update(dt);

    if (modelLoaded && !document.hidden && !isProductModeFn()) {
      renderer.render(scene, camera);
    }

    rafId = requestAnimationFrame(tick);
  }

  rafId = requestAnimationFrame(tick);

  return {
    wrapper,
    dispose() {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      renderer.dispose();

      if (renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }

      if (wrapper.parentNode) {
        wrapper.parentNode.removeChild(wrapper);
      }

      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();

        if (obj.material) {
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((m) => m.dispose && m.dispose());
        }
      });
    }
  };
}


function cleanModelUrl(raw) {
  let modelUrl = (raw || '').trim();
  if (!modelUrl) return '';

  const secondHttps = modelUrl.indexOf('https://', 1);
  const secondHttp = modelUrl.indexOf('http://', 1);
  let cutAt = -1;

  if (secondHttps > 0 && secondHttp > 0) cutAt = Math.min(secondHttps, secondHttp);
  else if (secondHttps > 0) cutAt = secondHttps;
  else if (secondHttp > 0) cutAt = secondHttp;

  if (cutAt > 0) modelUrl = modelUrl.substring(0, cutAt);

  return modelUrl;
}

function initInteractiveLookbook3D(container = document) {
  const section =
    container.querySelector('#interactive-lookbook') ||
    document.querySelector('#interactive-lookbook');

  if (!section) return;

  const items = Array.from(section.querySelectorAll('.look-item'));
  if (!items.length) return;

  const detail = section.querySelector('#lookbook-detail');

  items.forEach((item) => {
    if (item.dataset.modelStarted === 'true') return;

    const modelUrl = cleanModelUrl(item.dataset.modelUrl);
    if (!modelUrl) return;

    item.dataset.modelStarted = 'true';

    const modelScale = parseFloat(item.dataset.modelScale || '100');
    const modelYOffset = parseFloat(item.dataset.modelYOffset || '0');

    try {
      item._modelViewer = createModelViewer({
        container: item,
        url: modelUrl,
        modelScale,
        modelYOffset,
        isProductModeFn: () => detail && detail.classList.contains('is-product-mode'),
        onModelReady: () => {
          item.classList.add('has-3d');
        }
      });
    } catch (error) {
      console.warn('[Lookbook 3D] Optional 3D failed. Falling back to image.', error);
      item.dataset.modelStarted = 'false';
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  try {
    initInteractiveLookbook3D(document);
  } catch (error) {
    console.warn('[Lookbook 3D] Optional 3D init failed. Base lookbook still works.', error);
  }
});

document.addEventListener('shopify:section:load', (event) => {
  try {
    initInteractiveLookbook3D(event.target);
  } catch (error) {
    console.warn('[Lookbook 3D] Optional 3D section init failed.', error);
  }
});

document.addEventListener('shopify:section:reorder', () => {
  try {
    initInteractiveLookbook3D(document);
  } catch (error) {
    console.warn('[Lookbook 3D] Optional 3D reorder init failed.', error);
  }
});

document.addEventListener('shopify:section:select', (event) => {
  try {
    initInteractiveLookbook3D(event.target);
  } catch (error) {
    console.warn('[Lookbook 3D] Optional 3D select init failed.', error);
  }
});

document.addEventListener('shopify:block:select', () => {
  try {
    initInteractiveLookbook3D(document);
  } catch (error) {
    console.warn('[Lookbook 3D] Optional 3D block init failed.', error);
  }
});
